/**
 * FloatingTrialBubble - A prominent floating "Request free trial" button
 * that sticks to the bottom-right corner and opens the TrialRequestDialog.
 *
 * Drop this near the top of any page that should show it.
 */
import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import TrialRequestDialog from '@/components/landing/TrialRequestDialog';

export default function FloatingTrialBubble() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <style>{`
        @keyframes p4s-bubble-pulse {
          0%, 100% { box-shadow: 0 8px 24px rgba(37, 99, 235, 0.35), 0 0 0 0 rgba(37, 99, 235, 0.55); }
          50%      { box-shadow: 0 10px 28px rgba(37, 99, 235, 0.45), 0 0 0 12px rgba(37, 99, 235, 0); }
        }
        @keyframes p4s-bubble-sparkle {
          0%, 100% { transform: rotate(0deg) scale(1); }
          50%      { transform: rotate(15deg) scale(1.15); }
        }
        .p4s-trial-bubble {
          position: fixed;
          right: max(1.25rem, env(safe-area-inset-right));
          bottom: max(1.25rem, env(safe-area-inset-bottom));
          z-index: 900;
          display: inline-flex;
          align-items: center;
          gap: 0.55rem;
          padding: 0.85rem 1.35rem;
          background: linear-gradient(135deg, #1e40af 0%, #2563eb 55%, #06b6d4 100%);
          color: #fff;
          border: none;
          border-radius: 999px;
          font-family: inherit;
          font-size: 0.95rem;
          font-weight: 700;
          letter-spacing: 0.01em;
          cursor: pointer;
          box-shadow: 0 8px 24px rgba(37, 99, 235, 0.35);
          animation: p4s-bubble-pulse 2.6s ease-in-out infinite;
          transition: transform 0.15s ease;
          text-decoration: none;
        }
        .p4s-trial-bubble:hover {
          transform: translateY(-2px) scale(1.03);
        }
        .p4s-trial-bubble:focus-visible {
          outline: 3px solid #fff;
          outline-offset: 3px;
        }
        .p4s-trial-bubble__icon {
          animation: p4s-bubble-sparkle 2.2s ease-in-out infinite;
        }
        @media (max-width: 480px) {
          .p4s-trial-bubble {
            font-size: 0.85rem;
            padding: 0.7rem 1rem;
            right: 0.85rem;
            bottom: 0.85rem;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .p4s-trial-bubble,
          .p4s-trial-bubble__icon {
            animation: none;
          }
        }
      `}</style>

      <button
        type="button"
        className="p4s-trial-bubble"
        onClick={() => setOpen(true)}
        data-testid="floating-trial-bubble"
        aria-label="Request a free trial account"
      >
        <Sparkles size={18} className="p4s-trial-bubble__icon" aria-hidden="true" />
        Request Free Trial
      </button>

      <TrialRequestDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
